import { CompanyCreateForm, type CompanyLogoDraft } from '@/components/company-create-form';
import { companiesApi } from '@/lib/api';
import { sameLogoFraming } from '@/lib/logo-framing';
import { flagLogoWarning } from '@/lib/logo-warning';
import { type CreateCompanyInput, NEUTRAL_LOGO_FRAMING } from '@mss_creator/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/companies/new')({
  component: NewCompanyPage,
});

/**
 * Creates the company, then sends the logo chosen on the form (issue #20): the
 * logo needs a company to belong to. A failure to send it does not undo the
 * creation — the company exists — it is only remembered, so that the company's
 * page opens with a warning (H11).
 */
export async function createCompanyWithLogo(
  input: CreateCompanyInput,
  logo: CompanyLogoDraft | null,
) {
  const company = await companiesApi.create(input);
  let logoSaved = true;
  if (logo) {
    try {
      await companiesApi.uploadLogo(company.id, logo.file);
      if (!sameLogoFraming(logo.framing, NEUTRAL_LOGO_FRAMING)) {
        await companiesApi.saveLogoFraming(company.id, logo.framing);
      }
    } catch {
      logoSaved = false;
    }
  }
  return { company, logoSaved };
}

function NewCompanyPage() {
  const navigate = useNavigate();
  const { mutate, isPending, isError } = useMutation({
    mutationFn: ({ input, logo }: { input: CreateCompanyInput; logo: CompanyLogoDraft | null }) =>
      createCompanyWithLogo(input, logo),
    onSuccess: ({ company, logoSaved }) => {
      if (!logoSaved) flagLogoWarning(company.id);
      void navigate({ to: '/companies/$companyId', params: { companyId: company.id } });
    },
  });

  return (
    <section className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="font-bold text-bleu-nuit text-h2">Nouvelle entreprise</h1>
        <p className="text-gray-600 text-small">
          Nom et noyau requis — le reste (setup de singularité, paliers, contexte) se complète après
          coup.
        </p>
      </div>
      {isError && (
        <p className="text-danger text-small">
          Impossible de créer l'entreprise. Réessayez dans un instant.
        </p>
      )}
      <CompanyCreateForm onSubmit={(input, logo) => mutate({ input, logo })} />
      {isPending && <p className="text-gray-500 text-small">Création…</p>}
    </section>
  );
}
